import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { PLACEMENT } from './appSlice'

export interface CrownInput {
  backgroundcolor?: string | number | null[]
  fullmonolithiceffect: string
  protocol: string
  stainingbrand: string
  x?: number
  y?: number
  crownPlacement?: string
  targetcolor: string[]
}

export interface StumpShadeInput {
  backgroundcolor: string | number | null[]
  basecolor: string
  protocol: string
  stainingbrand: string
  targetcolor: string[]
  x?: number
  y?: number
  stumpShadePlacement?: string
}

interface CrownRecipe {
  [PLACEMENT.LEFT]: { [key: string]: { input: CrownInput; output: any } }
  [PLACEMENT.RIGHT]: { [key: string]: { input: CrownInput; output: any } }
}

interface StumpShadeRecipe {
  [PLACEMENT.LEFT]: { [key: string]: { input: StumpShadeInput; output: any } }
  [PLACEMENT.RIGHT]: { [key: string]: { input: StumpShadeInput; output: any } }
}

export const GINGIVA_STAINING_REGIONS = {
  region1: 'gingiva.regions.region1',
  region2: 'gingiva.regions.region2',
  region3: 'gingiva.regions.region3',
}

export interface StainingStudioState {
  selectedCrownKey: string | null
  crownRecipe: CrownRecipe | null
  selectedCrownStainingBrand: string | null
  //GINGIVA
  gingivastainingbrand: string
  selectedGingivaStainingKey: string
  gingivaStaing: {
    [imageId: string]: {
      [key: string]: {
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }
    }
  }

  gingivaRecipeOutput: any
  selectedStumpShadeBrand: string | null
  selectedStumpShadeKey: string | null
  stumpShadeRecipe: StumpShadeRecipe | null
  washBake: any
  selectedWashbakeKey: string
  washbakeStainingBrand: string
  selectedWashbakeFrameworkKey: string
  frameworkTargetColor: any
  washBakeRecipeOutput: any
  gingivaTarget: any
  availableSpace: string | null
}

const initialState: StainingStudioState = {
  selectedCrownKey: null,
  crownRecipe: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },
  selectedCrownStainingBrand: null,
  //GINGIVA
  selectedGingivaStainingKey: '',
  gingivastainingbrand: '',
  gingivaStaing: {},
  gingivaRecipeOutput: {},
  gingivaTarget: {},

  //stump shade
  selectedStumpShadeBrand: null,
  selectedStumpShadeKey: null,
  stumpShadeRecipe: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },

  //Wash bake
  washbakeStainingBrand: '',
  washBake: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },
  selectedWashbakeKey: '',
  selectedWashbakeFrameworkKey: '',
  frameworkTargetColor: {},
  washBakeRecipeOutput: {},
  availableSpace: null,
}

export const stainingStudioSlice = createSlice({
  name: 'stainingStudio',
  initialState,
  reducers: {
    setSelectedCrownKey: (state, action: PayloadAction<string | null>) => {
      return { ...state, selectedCrownKey: action.payload }
    },
    setSelectedCrownStainingBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return { ...state, selectedCrownStainingBrand: action.payload }
    },
    setCrownRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: CrownInput
          output?: any
          generation_id?: string | number
        }
      }>,
    ) => {
      const placement = action.payload.placement as keyof CrownRecipe
      const currentRecipe = state.crownRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        crownRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
            },
          },
        },
      }
    },
    clearStainingStudioCrownState: () => initialState,
    removeCrownRecipeKey: (state, action: PayloadAction<{ key: string }>) => {
      if (state?.crownRecipe?.[PLACEMENT.LEFT]) {
        delete state.crownRecipe[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.crownRecipe?.[PLACEMENT.RIGHT]) {
        delete state.crownRecipe[PLACEMENT.RIGHT][action.payload.key]
      }
    },

    //GINGIVA REDUCER
    setGingivaStainingBrand: (state, action: PayloadAction<string>) => {
      state.gingivastainingbrand = action.payload
    },
    setSelectedGingivaStainingKey: (state, action: PayloadAction<string>) => {
      state.selectedGingivaStainingKey = action.payload
    },
    setGingivaStainingTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
        key?: string
      }>,
    ) => {
      const {
        imageId,
        targetcolor,
        position,
        output,
        key: actionKey,
      } = action.payload
      const key = actionKey || state.selectedGingivaStainingKey
      if (!key || !imageId) return

      if (!state.gingivaStaing) {
        state.gingivaStaing = {}
      }

      if (!state.gingivaStaing[imageId]) {
        state.gingivaStaing[imageId] = {}
      }

      if (!state.gingivaStaing[imageId][key]) {
        state.gingivaStaing[imageId][key] = {}
      }

      const existing = state.gingivaStaing[imageId][key]

      state.gingivaStaing[imageId][key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && { output: { ...existing.output, ...output } }),
      }
    },

    clearGingivaStainingState: (state) => {
      return {
        ...state,
        selectedGingivaStainingKey: initialState.selectedGingivaStainingKey,
        gingivastainingbrand: initialState.gingivastainingbrand,
        gingivaStaing: initialState.gingivaStaing,
      }
    },
    setGingivaRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.gingivaRecipeOutput) {
        state.gingivaRecipeOutput = {}
      }

      if (!state.gingivaRecipeOutput[imageId]) {
        state.gingivaRecipeOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.gingivaRecipeOutput[imageId][key]
      } else {
        state.gingivaRecipeOutput[imageId][key] = result
      }
    },

    removeGingivaStainingTarget: (
      state,
      action: PayloadAction<{ imageId: string; key: string }>,
    ) => {
      const { imageId, key } = action.payload

      if (state.gingivaStaing?.[imageId]?.[key]) {
        delete state.gingivaStaing[imageId][key]

        if (Object.keys(state.gingivaStaing[imageId]).length === 0) {
          delete state.gingivaStaing[imageId]
        }
      }

      if (state.gingivaTarget?.[imageId]?.[key]) {
        delete state.gingivaTarget[imageId][key]
        if (Object.keys(state.gingivaTarget[imageId]).length === 0) {
          delete state.gingivaTarget[imageId]
        }
      }
    },

    //stump shade
    setSelectedStumpShadeStainingBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return { ...state, selectedStumpShadeBrand: action.payload }
    },
    setSelectedStumpShadeKey: (state, action: PayloadAction<string | null>) => {
      return { ...state, selectedStumpShadeKey: action.payload }
    },
    setStumpShadeRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: StumpShadeInput
          output?: any
          generation_id?: string | number
        }
      }>,
    ) => {
      const placement = action.payload.placement as keyof StumpShadeRecipe
      const currentRecipe = state.stumpShadeRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        stumpShadeRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
            },
          },
        },
      }
    },
    removeStumpShadeRecipeKey: (
      state,
      action: PayloadAction<{ key: string }>,
    ) => {
      if (state?.stumpShadeRecipe?.[PLACEMENT.LEFT]) {
        delete state.stumpShadeRecipe[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.stumpShadeRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stumpShadeRecipe[PLACEMENT.RIGHT][action.payload.key]
      }
    },
    clearStumpShadeState: () => initialState,

    //WASHBAKE
    setWashBakeStainingBrand: (state, action: PayloadAction<string>) => {
      state.washbakeStainingBrand = action.payload
    },
    setSelectedWashbakeKey: (state, action: PayloadAction<string>) => {
      state.selectedWashbakeKey = action.payload
    },

    setWashbakeTarget: (
      state,
      action: PayloadAction<{
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { targetcolor, position, output } = action.payload
      const key = state.selectedWashbakeKey
      if (!key) return

      if (!state.washBake) {
        state.washBake = {}
      }

      const existing = state.washBake[key] || {}

      state.washBake[key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && { output: { ...existing.output, ...output } }),
      }
    },

    setSelectedWashbakeFrameworkKey: (state, action: PayloadAction<string>) => {
      state.selectedWashbakeFrameworkKey = action.payload
    },

    setWashBakeFrameWorkTarget: (
      state,
      action: PayloadAction<{
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload
      state.frameworkTargetColor[position] = { targetcolor, imagePosition }
    },

    setWashBakeRecipeOutput: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: { input?: any; output?: any; generation_id?: string | number }
      }>,
    ) => {
      const { placement, key, data } = action.payload

      const currentRecipe = state.washBake || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        washBake: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [key]: {
              ...(currentRecipe[placement]?.[key] || {}),
              ...data,
            },
          },
        },
      }
    },
    removeWashBakeTarget: (state, action: PayloadAction<{ key: string }>) => {
      if (state?.washBake?.[PLACEMENT.LEFT]) {
        delete state.washBake[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.washBake?.[PLACEMENT.RIGHT]) {
        delete state.washBake[PLACEMENT.RIGHT][action.payload.key]
      }
    },

    setAvailableSpace(state, action: PayloadAction<string>) {
      state.availableSpace = action.payload
    },

    setStainingGingivaTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        key: string
        targetcolor: number[]
      }>,
    ) => {
      const { imageId, key, targetcolor } = action.payload
      if (!imageId || !key) return

      if (!state.gingivaTarget[imageId]) {
        state.gingivaTarget[imageId] = {}
      }

      state.gingivaTarget[imageId][key] = targetcolor
    },
    clearWashBakeState: () => initialState,
  },
})

export const {
  setSelectedCrownKey,
  setSelectedCrownStainingBrand,
  setCrownRecipe,
  clearStainingStudioCrownState,
  removeCrownRecipeKey,
  //GINGIVA
  setSelectedGingivaStainingKey,
  setGingivaStainingTarget,
  setGingivaStainingBrand,
  clearGingivaStainingState,
  setGingivaRecipeOutput,
  removeGingivaStainingTarget,
  setStainingGingivaTargetColor,

  //stump shade
  setSelectedStumpShadeStainingBrand,
  setSelectedStumpShadeKey,
  setStumpShadeRecipe,
  removeStumpShadeRecipeKey,
  clearStumpShadeState,

  //WASHBAKE
  setWashBakeStainingBrand,
  setSelectedWashbakeKey,
  setWashbakeTarget,
  setSelectedWashbakeFrameworkKey,
  setWashBakeFrameWorkTarget,
  setWashBakeRecipeOutput,
  removeWashBakeTarget,
  setAvailableSpace,
  clearWashBakeState,
} = stainingStudioSlice.actions

export default stainingStudioSlice.reducer
